
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { AdminSearch } from "./shared/AdminSearch";
import { AdminFilters } from "./shared/AdminFilters";
import { AdminPagination } from "./shared/AdminPagination";
import { usePartnersManager } from "@/hooks/usePartnersManager";
import { PartnerEditDialog } from "./partners/PartnerEditDialog";
import { PartnersList } from "./partners/PartnersList";

export const PartnersManager = () => {
  const [editingPartner, setEditingPartner] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
  });

  const {
    loading,
    saving,
    filteredPartners,
    pagination,
    handleSave,
    deletePartner,
  } = usePartnersManager({ searchTerm, filters });

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    pagination.resetPagination();
  };

  const handleResetFilters = () => {
    setFilters({ status: "" });
    setSearchTerm("");
    pagination.resetPagination();
  };

  const handleDelete = async (partnerId: string) => {
    await deletePartner(partnerId);
  };

  const handleEdit = (partner: any) => {
    setEditingPartner(partner);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingPartner(null);
    setDialogOpen(true);
  };

  const handleDialogSave = async (partnerData: any) => {
    // Include the partner ID if we're editing
    const dataWithId = editingPartner 
      ? { ...partnerData, id: editingPartner.id }
      : partnerData;
    
    await handleSave(dataWithId);
    setEditingPartner(null);
    setDialogOpen(false);
  };

  const handleDialogClose = () => {
    setEditingPartner(null);
    setDialogOpen(false);
  };

  if (loading) {
    return <div className="text-center">Loading partners...</div>;
  }

  const hasFilters = Boolean(searchTerm || Object.values(filters).some(v => v));

  return (
    <div className="space-y-4 md:space-y-6 max-w-full overflow-hidden">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Partners Management</h2>
        <Button onClick={handleCreate} disabled={saving}>
          <Plus className="h-4 w-4 mr-2" />
          Add Partner
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <AdminSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search partners..."
          className="max-w-md"
        />
        
        <AdminFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
        />
      </div>

      <PartnerEditDialog
        partner={editingPartner}
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        onSave={handleDialogSave}
        saving={saving}
      />

      <PartnersList
        partners={pagination.paginatedItems}
        onEdit={handleEdit}
        onDelete={handleDelete}
        saving={saving}
        hasFilters={hasFilters}
      />

      <AdminPagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        itemsPerPage={10}
        totalItems={filteredPartners.length}
        onPageChange={pagination.goToPage}
        onItemsPerPageChange={() => {}} // Fixed items per page for now
      />
    </div>
  );
};
